import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Check, CircleAlert, CircleX } from 'lucide-react';

import { AuthSplitLayout } from '@/components/layout/AuthSplitLayout';
import { Field } from '@/components/common/Field';
import { Button, Alert, Spinner } from '@agion/ui';

import apiClient from '@/services/api';

interface DeviceCodeInfo {
  user_code: string;
  client_id: string;
  scope: string;
  expires_at: string;
}

type PageState = 'input' | 'loading' | 'confirm' | 'success' | 'denied' | 'error';

// The device user code format (XXXX-XXXX); a format sample, the same in every language.
const USER_CODE_FORMAT_EXAMPLE = 'ABCD-1234';

export function DevicePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const codeFromUrl = searchParams.get('user_code') ?? '';

  const [pageState, setPageState] = useState<PageState>('input');
  const [userCode, setUserCode] = useState(codeFromUrl);
  const [deviceInfo, setDeviceInfo] = useState<DeviceCodeInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const verifyCode = useCallback(async (code: string) => {
    setPageState('loading');
    setError(null);

    try {
      const response = await fetch(
        `${apiClient.getBaseURL()}/device/verify?user_code=${encodeURIComponent(code)}`,
        { credentials: 'include' }
      );

      if (!response.ok) {
        const data = await response.json();
        if (response.status === 404) {
          setError(t('device.invalidCode'));
        } else {
          setError(data.error || t('device.verifyError'));
        }
        setPageState('error');
        return;
      }

      const data: DeviceCodeInfo = await response.json();
      setDeviceInfo(data);
      setPageState('confirm');
    } catch (err) {
      console.error('Failed to verify device code:', err);
      setError(t('device.verifyError'));
      setPageState('error');
    }
  }, [t]);

  // Check for user_code in URL
  useEffect(() => {
    if (codeFromUrl) {
      const timeout = window.setTimeout(() => {
        void verifyCode(codeFromUrl);
      }, 0);
      return () => window.clearTimeout(timeout);
    }
  }, [codeFromUrl, verifyCode]);

  const handleSubmitCode = (e: React.FormEvent) => {
    e.preventDefault();
    const normalizedCode = userCode.toUpperCase().trim();
    if (normalizedCode.length >= 8) {
      verifyCode(normalizedCode);
    }
  };

  const handleApprove = async () => {
    if (!deviceInfo) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${apiClient.getBaseURL()}/device/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ user_code: deviceInfo.user_code }),
      });

      if (!response.ok) {
        const data = await response.json();
        if (response.status === 401) {
          // User not authenticated - redirect to login with return_to
          const returnTo = `/device?user_code=${encodeURIComponent(deviceInfo.user_code)}`;
          navigate(`/login?return_to=${encodeURIComponent(returnTo)}`);
          return;
        }
        throw new Error(data.error || 'Failed to approve device');
      }

      setPageState('success');
    } catch (err) {
      console.error('Failed to approve device:', err);
      setError(t('device.approveError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeny = async () => {
    if (!deviceInfo) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${apiClient.getBaseURL()}/device/deny`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ user_code: deviceInfo.user_code }),
      });

      if (!response.ok) {
        const data = await response.json();
        if (response.status === 401) {
          // User not authenticated - redirect to login
          const returnTo = `/device?user_code=${encodeURIComponent(deviceInfo.user_code)}`;
          navigate(`/login?return_to=${encodeURIComponent(returnTo)}`);
          return;
        }
        throw new Error(data.error || 'Failed to deny device');
      }

      setPageState('denied');
    } catch (err) {
      console.error('Failed to deny device:', err);
      setError(t('device.denyError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTryAgain = () => {
    setPageState('input');
    setUserCode('');
    setDeviceInfo(null);
    setError(null);
  };

  const formatScope = (scope: string): string[] => {
    return scope.split(' ').filter(s => s.length > 0);
  };

  const getScopeLabel = (scope: string): string => {
    const labels: Record<string, string> = {
      openid: t('device.scopes.openid'),
      profile: t('device.scopes.profile'),
      email: t('device.scopes.email'),
      offline_access: t('device.scopes.offlineAccess'),
    };
    return labels[scope] || scope;
  };

  // Render code input form
  const renderInputForm = () => (
    <>
      <div className="lp-heading-block lp-rise lp-rise-2">
        <h2 className="lp-heading">{t('device.title')}</h2>
        <p className="lp-subheading">{t('device.description')}</p>
      </div>

      <form onSubmit={handleSubmitCode} className="lp-form lp-rise lp-rise-3">
        <div className="lp-code">
          <Field
            id="user_code"
            type="text"
            label={t('device.codeLabel')}
            value={userCode}
            onChange={(e) => setUserCode(e.target.value.toUpperCase())}
            placeholder={USER_CODE_FORMAT_EXAMPLE}
            maxLength={9}
            autoComplete="off"
            hint={t('device.codeHint')}
          />
        </div>

        <Button
          type="submit"
          className="lp-btn-primary w-full"
          disabled={userCode.length < 8}
        >
          {t('device.continue')}
          <ArrowRight size={16} aria-hidden="true" />
        </Button>
      </form>
    </>
  );

  // Render loading state
  const renderLoading = () => (
    <div className="lp-state" role="status">
      <Spinner size="lg" />
      <p className="lp-state-msg">{t('device.verifying')}</p>
    </div>
  );

  // Render confirmation dialog
  const renderConfirm = () => (
    <>
      <div className="lp-heading-block lp-rise lp-rise-2">
        <h2 className="lp-heading">{t('device.confirmTitle')}</h2>
        <p className="lp-subheading">{t('device.confirmDescription')}</p>
      </div>

      <div className="lp-rise lp-rise-3">
        {error && (
          <Alert variant="error" className="lp-alert">
            {error}
          </Alert>
        )}

        {deviceInfo && (
          <>
            <div className="lp-form">
              {/* Code display */}
              <div className="lp-chip">
                <div>
                  <div className="lp-chip-label">{t('device.codeLabel')}</div>
                  <div className="lp-chip-email lp-chip-code">{deviceInfo.user_code}</div>
                </div>
              </div>

              {/* Client info */}
              <div className="lp-chip">
                <div>
                  <div className="lp-chip-label">{t('device.requestingApp')}</div>
                  <div className="lp-chip-email">{deviceInfo.client_id}</div>
                </div>
              </div>
            </div>

            {/* Permissions */}
            <div className="lp-panel">
              <h3 className="lp-panel-title">{t('device.permissions')}</h3>
              <ul className="lp-panel-list">
                {formatScope(deviceInfo.scope).map((scope) => (
                  <li key={scope} className="lp-panel-item">
                    <Check size={16} aria-hidden="true" />
                    {getScopeLabel(scope)}
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}

        <hr className="lp-state-divider" />

        {/* Actions */}
        {/* Deny comes first, as before the layout move: a device code can be phished. */}
        <div className="lp-form">
          <Button
            onClick={handleDeny}
            variant="outline"
            className="lp-btn-social"
            disabled={isSubmitting}
          >
            {t('device.deny')}
          </Button>
          <Button
            onClick={handleApprove}
            className="lp-btn-primary w-full"
            disabled={isSubmitting}
            isLoading={isSubmitting}
          >
            {t('device.approve')}
          </Button>
        </div>

        <p className="lp-support">{t('device.securityWarning')}</p>
      </div>
    </>
  );

  // Render success state
  const renderSuccess = () => (
    <div className="lp-state lp-pop" role="status">
      <div className="lp-badge lp-badge-success">
        <Check size={24} aria-hidden="true" />
      </div>
      <h2 className="lp-state-title">{t('device.successTitle')}</h2>
      <p className="lp-state-msg">{t('device.successDescription')}</p>
      <p className="lp-note">{t('device.closeWindow')}</p>
    </div>
  );

  // Render denied state
  const renderDenied = () => (
    <div className="lp-state lp-pop" role="status">
      <div className="lp-badge lp-badge-brand">
        <CircleX size={24} aria-hidden="true" />
      </div>
      <h2 className="lp-state-title">{t('device.deniedTitle')}</h2>
      <p className="lp-state-msg">{t('device.deniedDescription')}</p>
      <p className="lp-note">{t('device.closeWindow')}</p>
    </div>
  );

  // Render error state
  const renderError = () => (
    <div className="lp-state lp-pop" role="alert">
      <div className="lp-badge lp-badge-brand">
        <CircleAlert size={24} aria-hidden="true" />
      </div>
      <h2 className="lp-state-title">{t('device.errorTitle')}</h2>
      <p className="lp-state-msg">{error || t('device.genericError')}</p>
      <hr className="lp-state-divider" />
      <Button onClick={handleTryAgain} variant="outline" className="lp-btn-social">
        {t('device.tryAgain')}
      </Button>
    </div>
  );

  return (
    <AuthSplitLayout>
      {pageState === 'input' && renderInputForm()}
      {pageState === 'loading' && renderLoading()}
      {pageState === 'confirm' && renderConfirm()}
      {pageState === 'success' && renderSuccess()}
      {pageState === 'denied' && renderDenied()}
      {pageState === 'error' && renderError()}
    </AuthSplitLayout>
  );
}

export default DevicePage;
